import { useState } from 'preact/hooks';
import { api, me, ApiErr, type Teacher } from '@/lib/teaching/teacher-api';
import { Card } from '@/components/teaching/ui';
import { Btn, ErrorLine, Field, inputCls, val } from '@/components/teaching/form';

// Slice 5 §03: change password (Settings) and the forced change after a
// reset-password. The server enforces length, difference and rate limits.

const MIN = 10;

function PasswordForm({ onDone, submitLabel }: { onDone: (t: Teacher) => void; submitLabel: string }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: Event) {
    e.preventDefault();
    setErr(null);
    if (next.length < MIN) { setErr(`New password must be at least ${MIN} characters`); return; }
    if (next !== again) { setErr('The two new passwords do not match'); return; }
    setBusy(true);
    try {
      const r = await api<{ teacher: Teacher }>('PUT', '/api/auth/password', { current, next });
      setCurrent(''); setNext(''); setAgain('');
      onDone(r.teacher);
    } catch (x) { setErr(x instanceof ApiErr ? x.error : 'Could not reach the server'); }
    setBusy(false);
  }

  return (
    <form onSubmit={submit} class="flex flex-col gap-3 max-w-[360px]">
      <Field label="Current password">
        <input type="password" autocomplete="current-password" required class={inputCls} value={current} onInput={(e) => setCurrent(val(e))} />
      </Field>
      <Field label={`New password (at least ${MIN} characters)`}>
        <input type="password" autocomplete="new-password" required class={inputCls} value={next} onInput={(e) => setNext(val(e))} />
      </Field>
      <Field label="New password again">
        <input type="password" autocomplete="new-password" required class={inputCls} value={again} onInput={(e) => setAgain(val(e))} />
      </Field>
      <ErrorLine error={err} />
      <Btn kind="primary" type="submit" disabled={busy}>{busy ? 'Saving...' : submitLabel}</Btn>
    </form>
  );
}

/** Shown instead of the app while must_change_password is set. */
export function ForcedPasswordChange() {
  return (
    <div class="min-h-full grid place-items-center px-4 py-10">
      <div class="w-full max-w-[400px] rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6 flex flex-col gap-4">
        <div>
          <h1 class="text-[18px] font-semibold text-[var(--color-text)] leading-tight">Choose a new password</h1>
          <div class="text-[12.5px] text-[var(--color-text-muted)] mt-1">
            Your password was reset. Enter the one-time password you were given, then choose your own.
          </div>
        </div>
        <PasswordForm submitLabel="Save new password" onDone={(t) => { me.value = t; location.assign('/teaching'); }} />
        <button type="button" class="text-[12px] text-[var(--color-text-muted)] underline self-start"
          onClick={() => { void api('POST', '/api/auth/logout').finally(() => { me.value = null; location.assign('/login'); }); }}>
          Sign out
        </button>
      </div>
    </div>
  );
}

export function ChangePasswordCard() {
  const [msg, setMsg] = useState<string | null>(null);
  return (
    <Card title="Change password" sub="Signs you out everywhere else; this device stays signed in.">
      <PasswordForm submitLabel="Change password" onDone={(t) => { me.value = t; setMsg('Password changed.'); }} />
      {msg && <div class="text-[12.5px] text-[#15803d] mt-2">{msg}</div>}
    </Card>
  );
}
